import React, { useEffect, useContext } from 'react';
import AppContext from 'contexts/appContext';
import Panel from 'components/common/panel/panel';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Heading from 'components/common/heading/heading';
import { Form } from 'react-bootstrap';
import useForm from 'hooks/form';
import { useHistory } from 'hooks/router';
import { ShowNotifyPopup } from 'components/common/popup/popup';
import BoardingContext from 'components/navigation/boarding/boardingContext';
import BoardingHelper from 'components/navigation/boarding/boardingHelper';
import BoardingService from 'services/boardingService';
import ApplicationService from 'services/applicationService';
import './boardingWizardPage.css';

import BoardingStepsMenu from 'components/navigation/boarding/wizard/controls/boardingStepsMenu';
import BoardingDealerSelection from 'components/navigation/boarding/wizard/controls/boardingDealerSelection';
import BoardingStoreInformation from 'components/navigation/boarding/wizard/controls/boardingStoreInformation';
import BoardingUserInformation from 'components/navigation/boarding/wizard/controls/boardingUserInformation';
import BoardingOrderInformation from 'components/navigation/boarding/wizard/controls/boardingOrderInformation';

export default function BoardingWizardPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);
  const { setFormRef, submitForm, resetForm, hasFormValidationError, renderFormValidationMessage } = useForm();
  const { boardingState, setStep } = useContext(BoardingContext);
  const step = boardingState.step;

  const history = useHistory();

  useEffect(() => {
    setAppArea(AppContext.Areas.Boarding, 'Board Sites for Free Trial');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Boarding' }, { name: 'Board New Sites', selected: true }]);
  }, []);

  const handleReturnToMenu = () => {
    resetForm();
    setStep(BoardingHelper.enums.steps.StepsMenu);
  };

  const handleNextStep = (step) => {
    submitForm(() => {
      resetForm();
      setStep(step);
    })();
  };

  const handleSelectDealer = (autoSelected) => {
    setStep(BoardingHelper.enums.steps.StepsMenu, {
      dealer: {
        isComplete: true,
        isEditable: !autoSelected,
        error: null,
      },
      store: {
        isEditable: true,
      },
      user: {
        isEditable: false,
      },
    });
  };

  const handleStoreSearch = () => {
    submitForm(() => {
      resetForm();
      handleNextStep(BoardingHelper.enums.steps.StoreSearch)
    })();
  };

  const handleRetryStore = () => {
    resetForm();
    setStep(BoardingHelper.enums.steps.StoreKeyEntry, {
      store: {
        isComplete: false,
        isEditable: true,
        error: null,
        search: null,
      },
    });
  };

  const handleSubmitStore = () => {
    submitForm(() => {
      resetForm();
      setStep(BoardingHelper.enums.steps.StepsMenu, {
        store: {
          isComplete: true,
          isEditable: true,
          error: null,
        },
        dealer: {
          isEditable: false,
        },
        user: {
          isEditable: true,
        },
      });
    })();
  };

  const handleUserSearch = () => {
    submitForm(() => {
      resetForm();
      handleNextStep(BoardingHelper.enums.steps.UserSearch)
    })();
  };

  const handleRetryUser = () => {
    resetForm();
    setStep(BoardingHelper.enums.steps.UserEmailEntry, {
      user: {
        isComplete: false,
        isEditable: true,
        error: null,
        search: null,
      },
    });
  };

  const handleSubmitUser = () => {
    submitForm(() => {
      resetForm();
      setStep(BoardingHelper.enums.steps.StepsMenu, {
        user: {
          isComplete: true,
          isEditable: true,
          error: null,
        },
        dealer: {
          isEditable: false,
        },
      });
    })();
  };

  const handleReportProblemStore = () => {
    setStep(BoardingHelper.enums.steps.StoreErrorReporting);
  };

  const handleSubmitProblemStore = () => {
    submitForm(() => {
      resetForm();
      ApplicationService.API.sendSupportMessage({
        email: boardingState.problem.email,
        subject: 'Boarding - Problem Reported',
        bodyText: `${boardingState.problem.details || ''}\n\n[Comments]\n${boardingState.problem.comments || ''}`
      });
      setStep(BoardingHelper.enums.steps.StoreKeyEntry, {
        store: {
          error: null,
          search: null,
        },
        problem: {
          email: null,
          details: null,
          comments: null,
        },
      });
      ShowNotifyPopup({
        title: 'Success',
        message: 'Message has been sent to POSiCloud Suppport',
        variant: 'success',
        yPosition: 'bottom',
        xPosition: 'center',
        seconds: 5,
      });
    })();
  };

  const handleReportProblemUser = () => {
    setStep(BoardingHelper.enums.steps.UserErrorReporting);
  };

  const handleSubmitProblemUser = () => {
    submitForm(() => {
      resetForm();
      ApplicationService.API.sendSupportMessage({
        email: boardingState.problem.email,
        subject: 'Boarding - Problem Reported',
        bodyText: `${boardingState.problem.details || ''}\n\n[Comments]\n${boardingState.problem.comments || ''}`
      });
      setStep(BoardingHelper.enums.steps.UserEmailEntry, {
        user: {
          error: null,
          search: null,
        },
        problem: {
          email: null,
          details: null,
          comments: null,
        },
      });
      ShowNotifyPopup({
        title: 'Success',
        message: 'Message has been sent to POSiCloud Suppport',
        variant: 'success',
        yPosition: 'bottom',
        xPosition: 'center',
        seconds: 5,
      });
    })();
  };

  const handleSubmit = async () => {
    const orderId = await BoardingService.API.insertOrder(boardingState.data);
    history.push(`/Boarding/Confirmation/${orderId}`);
  };

  const renderTitle = () => {
    switch (step) {
      case BoardingHelper.enums.steps.StepsMenu:
        return 'Boarding';
      case BoardingHelper.enums.steps.DealerSelection:
        return 'Dealer Selection';
      case BoardingHelper.enums.steps.StoreKeyEntry:
      case BoardingHelper.enums.steps.StoreSearch:
      case BoardingHelper.enums.steps.StoreDetails:
      case BoardingHelper.enums.steps.StoreError:
        return 'Store Information';
      case BoardingHelper.enums.steps.StoreErrorReporting:
        return 'Report a Problem';
      case BoardingHelper.enums.steps.UserEmailEntry:
      case BoardingHelper.enums.steps.UserSearch:
      case BoardingHelper.enums.steps.UserDetails:
      case BoardingHelper.enums.steps.UserFound:
      case BoardingHelper.enums.steps.UserError:
        return 'User Information';
      case BoardingHelper.enums.steps.UserErrorReporting:
        return 'Report a Problem';
      default: return null;
    }
  };

  const renderTools = () => {
    if (step === BoardingHelper.enums.steps.StepsMenu) {
      return null;
    }
    return <Inputs.Button variant='hover-default' iconClass='icons8-chevron-left' label='Menu' onClick={handleReturnToMenu} />;
  };

  const renderErrorMessage = () => {
    if (hasFormValidationError()) {
      return renderFormValidationMessage();
    }
    return null;
  };

  const renderStepsMessage = (alwaysShow) => {
    if (alwaysShow && getCompletedCount() === 3) {
      return <span>All steps completed. Review the information above, then press the <span className='text-thicker'>Submit</span> button below to finish</span>;
    }
    else if (alwaysShow || getCompletedCount() !== 3) {
      return <span>{getCompletedCount()} of 3 steps completed</span>;
    }
    return null;
  };

  const renderSubmitButton = () => {
    if (getCompletedCount() === 3) {
      return <Inputs.Button variant='success' onClick={handleSubmit} iconClass='icons8-curved-arrow' label='Submit' />;
    }
    return null;
  };

  const renderSpecialInstructionsForm = () => {
    if (boardingState.step === BoardingHelper.enums.steps.StepsMenu && getCompletedCount() === 3) {
      return (
        <Panel.Body>
          <BoardingOrderInformation />
        </Panel.Body>
      );
    }
    return null;
  };

  const getCompletedCount = () => {
    let count = 0;
    if (boardingState.dealer.isComplete) {
      count += 1;
    }
    if (boardingState.store.isComplete) {
      count += 1;
    }
    if (boardingState.user.isComplete) {
      count += 1;
    }
    return count;
  };

  const renderFooter = () => {
    switch (step) {
      case BoardingHelper.enums.steps.StepsMenu:
        return (
          <>
            <div className='mx-1 mb-2'>{renderStepsMessage(true)}</div>
            {renderSubmitButton()}
          </>
        );
      case BoardingHelper.enums.steps.DealerSelection:
        return <div className='mx-1 mb-2'>Select a dealer from the list above to continue</div>;
      case BoardingHelper.enums.steps.StoreKeyEntry:
        return (
          <>
            <div className='mx-1 mb-2'>Enter the license key number for the store, then press the <span className='text-thicker'>Next</span> button below to continue</div>
            {renderErrorMessage()}
            <Inputs.Button variant='app' onClick={handleStoreSearch} iconClass='icons8-double-right' label='Next' />
          </>
        );
      case BoardingHelper.enums.steps.StoreDetails:
        return (
          <>
            <div className='mb-2'>Enter the details for the new store, then press the <span className='text-thicker'>Save Store</span> button below to continue</div>
            {renderErrorMessage()}
            <Inputs.Button variant='default' onClick={handleRetryStore} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='success' onClick={handleSubmitStore} iconClass='icons8-save' label='Save Store' />
          </>
        );
      case BoardingHelper.enums.steps.StoreError:
        return (
          <>
            <Inputs.Button variant='default' onClick={handleRetryStore} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='danger' onClick={handleReportProblemStore} iconClass='icons8-about' label='Contact Support' />
          </>
        );
      case BoardingHelper.enums.steps.StoreErrorReporting:
        return (
          <>
            <Inputs.Button variant='default' onClick={handleRetryStore} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='danger' onClick={handleSubmitProblemStore} iconClass='icons8-curved-arrow' label='Send Message' />
          </>
        );
      case BoardingHelper.enums.steps.UserEmailEntry:
        return (
          <>
            <div className='mx-1 mb-2'>Enter the email address for the master user, then press the <span className='text-thicker'>Next</span> button below to continue</div>
            {renderErrorMessage()}
            <Inputs.Button variant='app' onClick={handleUserSearch} iconClass='icons8-double-right' label='Next' />
          </>
        );
      case BoardingHelper.enums.steps.UserDetails:
        return (
          <>
            <div className='mb-2'>Enter the details for the new master user, then press the <span className='text-thicker'>Save User</span> button below to continue</div>
            {renderErrorMessage()}
            <Inputs.Button variant='default' onClick={handleRetryUser} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='success' onClick={handleSubmitUser} iconClass='icons8-save' label='Save User' />
          </>
        );
      case BoardingHelper.enums.steps.UserFound:
        return (
          <>
            <div className='mb-2'>Confirm the details for master user above are correct, then press the <span className='text-thicker'>Confirm User</span> button below to continue</div>
            {renderErrorMessage()}
            <Inputs.Button variant='default' onClick={handleRetryUser} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='success' onClick={handleSubmitUser} iconClass='icons8-checkmark' label='Confirm User' />
          </>
        );
      case BoardingHelper.enums.steps.UserError:
        return (
          <>
            <Inputs.Button variant='default' onClick={handleRetryUser} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='danger' onClick={handleReportProblemUser} iconClass='icons8-about' label='Contact Support' />
          </>
        );
      case BoardingHelper.enums.steps.UserErrorReporting:
        return (
          <>
            <Inputs.Button variant='default' onClick={handleRetryUser} iconClass='icons8-double-left' label='Previous' />
            <Inputs.Button variant='danger' onClick={handleSubmitProblemUser} iconClass='icons8-curved-arrow' label='Send Message' />
          </>
        );
      default: return null;
    }
  };

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <div></div>
          <div className='flex-grow-1 d-flex justify-content-end'>
            {renderStepsMessage(false)}
            {renderSubmitButton()}
          </div>
        </Heading>
      </Flow.Header>
      <Flow.Body>
        <div className='row justify-content-md-center mt-4 mx-0'>
          <div className='col-sm-12 col-md-10 col-lg-9 col-xl-7'>
            <Form ref={setFormRef} noValidate>
              <Panel>
                <Panel.Header tools={renderTools()}>{renderTitle()}</Panel.Header>
                <Panel.Body>
                  <BoardingStepsMenu />
                  <BoardingDealerSelection onSelectDealer={handleSelectDealer} />
                  <BoardingStoreInformation onStoreSearch={handleStoreSearch} />
                  <BoardingUserInformation onUserSearch={handleUserSearch} />
                </Panel.Body>
                {renderSpecialInstructionsForm()}
                <Panel.Footer>
                  {renderFooter()}
                </Panel.Footer>
              </Panel>
            </Form>
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}